import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { FaChevronCircleRight, FaSignInAlt, FaCog } from "react-icons/fa";

import AuthActions from "@/store/ducks/auth";
import { Input } from "@/components/Form";
import { Page, DashboardContainer, Row, Col } from "@/components/Grid";
import { AdminMenu } from "@/components/Admin";
import { Navbar } from "@/components/Blocks";
import Paginator, { Loading } from "@/components/Paginator";
import PlanilhaEmpresas from "@/components/Planilhas/Empresas";
import { Modal } from "@/components/Common";
import ModalEditValor from "@/components/Empresa/ModalEditValor";

import { telefone, cnpj, cpf } from "@/formatters";

import {
  Header,
  FiltroBasico,
  FilterButtonContainer,
  Totais,
  EmpresaListItem,
  ActionButton,
} from "./styles";

export default function Empresas({ history, match }) {
  const dispatch = useDispatch();
  const [empresas, setEmpresas] = useState([]);

  const [openModal, setOpenModal] = useState(false);
  const [empresaId, setEmpresaId] = useState(null);

  const [readyToPaginate, setReadyToPaginate] = useState(false);
  const [query, setQuery] = useState({ filters: "empresa,todas" });

  const [paginator, setPaginator] = useState(null);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);

  function handleOnFetchComplete(data) {
    setEmpresas(data.data);
    setPaginator(data.paginator);
  }

  function handleOnChange(event) {
    const { name, value } = event.target;
    setQuery(state => {
      return { ...state, [name]: value };
    });
  }

  function renderHeader() {
    let titulo;

    const status = match.params.hasOwnProperty("status")
      ? match.params.status
      : null;

    switch (true) {
      case status === "comAvaliacoesPendentes":
        titulo = "Empresas com avaliações de contratos pendentes";
        break;
      default:
        titulo = "Todas as empresas";
    }

    return (
      <Header>
        <h4>Empresas</h4>
        <h2>{titulo}</h2>
        <div className="breadcrumbs">
          Empresas {">"} <span>{titulo}</span>
        </div>
      </Header>
    );
  }

  function renderFiltroBasico() {
    const colNome = {
      lg: 0.7,
      md: 0.5,
      sm: 1,
    };

    const colButton = {
      lg: 0.3,
      md: 0.5,
      sm: 1,
    };

    return (
      <FiltroBasico>
        <Row hascols>
          <Col {...colNome}>
            <Input
              label="Nome da empresa"
              name="nome"
              placeholder="Busque pelo nome da empresa"
              onChange={handleOnChange}
            />
          </Col>
          <Col {...colButton}>
            <FilterButtonContainer>
              <PlanilhaEmpresas block params={query} />
            </FilterButtonContainer>
          </Col>
        </Row>
      </FiltroBasico>
    );
  }

  function renderTotais() {
    if (loadingEmpresas) return <></>;
    if (paginator !== null) {
      const { total, pages } = paginator;
      if (total === 0) {
        return (
          <Totais>
            Sem empresas para exibir dentro dos critérios de busca
          </Totais>
        );
      }
      return (
        <Totais>
          Exibindo {empresas.length} de {total} empresas
        </Totais>
      );
    }
  }

  function handleAssumirControle(empresaId) {
    const data = {
      entidade_tipo: "empresa",
      entidade_id: empresaId,
    };
    dispatch(AuthActions.authAssumirControleRequest(data, history));
  }

  function handleVerPerfil(empresaId) {
    history.push(`/empresa/${empresaId}`);
  }

  function renderEmpresas() {
    return empresas.map(empresa => {
      return (
        <EmpresaListItem key={empresa.id}>
          <div className="coluna coluna-nome">
            <h4>{empresa.nome_exibicao}</h4>
            <p>
              {empresa.tipo_pessoa === "J" ? "CNPJ: " : "CPF:"}
              {empresa.tipo_pessoa === "J"
                ? cnpj(empresa.documento)
                : cpf(empresa.documento)}
            </p>
          </div>
          <div className="coluna coluna-responsavel">
            <h4>Responsável</h4>
            {empresa.responsavel_legal_nome}
          </div>
          <div className="coluna coluna-responsavel">
            <h4>Contato</h4>
            {telefone(empresa.responsavel_legal_telefone)}<br />
          </div>
          <div className="coluna coluna-responsavel">
            <h4>Valor</h4>
            {`R$${String(empresa.valor_contrato)}`} <br />
          </div>
          <div className="coluna coluna-actions">
            <ActionButton
              onClick={() => {
                setOpenModal(true);
                setEmpresaId(empresa.id);
              }}
            >
              <FaCog color="#271946" size={14} /> <span>Editar valor</span>
            </ActionButton>
            <ActionButton onClick={() => handleAssumirControle(empresa.id)}>
              <FaSignInAlt color="#271946" size={14} />{" "}
              <span>Assumir controle</span>
            </ActionButton>
            <ActionButton onClick={() => handleVerPerfil(empresa.id)}>
              <FaChevronCircleRight color="#271946" size={14} />{" "}
              <span>Ver Perfil</span>
            </ActionButton>
          </div>
        </EmpresaListItem>
      );
    });
  }

  const setQueryCB = useCallback(() => {
    const status = match.params.hasOwnProperty("status")
      ? `empresa,${match.params.status}`
      : "empresa,todas";
    setQuery(state => ({ ...state, ...(status && { filters: status }) }));
    setReadyToPaginate(true);
  }, [match.params, setQuery, setReadyToPaginate]);

  useEffect(() => {
    setQueryCB();
  }, [setQueryCB]);

  return (
    <Page>
      <Navbar type="interna" />
      <AdminMenu />
      <DashboardContainer>
        {renderHeader()}
        <Modal open={openModal}>
          <ModalEditValor Id={empresaId} onClose={() => setOpenModal(false)} />
        </Modal>
        {renderFiltroBasico()}
        {renderTotais()}
        <Loading loading={loadingEmpresas}>
          {renderEmpresas()}
          {readyToPaginate && (
            <Paginator
              endpoint="empresas"
              query={query}
              onFetchComplete={handleOnFetchComplete}
              onLoading={l => setLoadingEmpresas(l)}
            />
          )}
        </Loading>
      </DashboardContainer>
    </Page>
  );
}
